import React, {Fragment} from 'react';
import {Checkbox, Col, Form, Input, InputNumber, Modal, Radio, Row, Select} from "antd";
import {
  GUTTER_MD,
  MARGIN_MD,
  SPAN_FULL,
  SPAN_HALF,
  SPAN_ONE_QUARTER,
  SPAN_ONE_THIRD,
  SPAN_THREE_QUARTERS,
  SPAN_TWO_THIRDS
} from "../../../styles/constants";
import Selector from "../../../components/selector";
import {compose} from "redux";
import {translate} from 'react-i18next';
import {
  DSD_ATTRIBUTE_ATTACHMENT_LEVEL_DATASET,
  DSD_ATTRIBUTE_ATTACHMENT_LEVEL_GROUP,
  DSD_ATTRIBUTE_ATTACHMENT_LEVEL_OBSERVATION,
  DSD_ATTRIBUTE_ATTACHMENT_LEVEL_SERIES,
  DSD_DIMENSION_TYPE_FREQUENCY,
  DSD_DIMENSION_TYPE_MEASURE,
  DSD_DIMENSION_TYPE_NORMAL,
  DSD_DIMENSION_TYPE_TIME,
  getArtefactTripletFromUrn,
  getStringFromArtefactTriplet,
  getStringFromItemUrn
} from "../../../utils/sdmxJson";
import {ARTEFACT_FORM_MODE_EDIT, ARTEFACT_FORM_MODE_READ} from "../../../components/artefact-form";
import _ from "lodash";
import AnnotationsForm from "../../../components/annotations-form";
import {getSdmxDimensionTypesTranslations} from "./getSdmxDimensionTypesTranslations";
import {getSdmxAttributeAttachmentLevelTranslations} from "./getSdmxAttributeAttachmentLevelTranslations";
import LocalizedMonthPicker from "../../../components/localized-month-picker";
import moment from "moment";
import {
  TIME_DIMENSION_DEFAULT_ANNOTATION_MODE_LAST_N_PERIODS,
  TIME_DIMENSION_DEFAULT_ANNOTATION_MODE_RANGE
} from "../../../utils/annotations";
import {isIDValidWithHelp} from "../../../utils/artefactValidators";

const formItemLayout = {
  labelCol: {span: SPAN_ONE_QUARTER},
  wrapperCol: {span: SPAN_THREE_QUARTERS}
};

const mapPropsToFields = ({component}) => ({
  type: Form.createFormField({value: component ? component.type : null}),
  id: Form.createFormField({value: component ? component.id : null}),
  order: Form.createFormField({value: component ? component.order : null}),
  concept: Form.createFormField({
    value: component && component.concept ? getStringFromItemUrn(component.concept) : null
  }),
  representation: Form.createFormField({
    value: component && component.representation
      ? getStringFromArtefactTriplet(getArtefactTripletFromUrn(component.representation))
      : null
  }),
  hclTriplet: Form.createFormField({
    value: component && component.hclTriplet
      ? getStringFromArtefactTriplet(component.hclTriplet)
      : null
  }),
  hierId: Form.createFormField({
    value: component && component.hierId
      ? component.hierId
      : null
  }),
  groupDimensions: Form.createFormField({value: component ? (component.groupDimensions || []) : null}),
  assignmentStatus: Form.createFormField({value: component ? component.assignmentStatus : null}),
  attachmentLevel: Form.createFormField({value: component ? component.attachmentLevel : null}),
  attachedDimensions: Form.createFormField({value: component ? (component.attachedDimensions || []) : null}),
  attachmentGroup: Form.createFormField({value: component ? component.attachmentGroup : null})
});

export const DSD_COMPONENT_TYPE_MEASURE = 'DSD_COMPONENT_TYPE_MEASURE';
export const DSD_COMPONENT_TYPE_DIMENSION = 'DSD_COMPONENT_TYPE_DIMENSION';
export const DSD_COMPONENT_TYPE_GROUP = 'DSD_COMPONENT_TYPE_GROUP';
export const DSD_COMPONENT_TYPE_ATTRIBUTE = 'DSD_COMPONENT_TYPE_ATTRIBUTE';

const onFieldsChange = (props, fields) =>
  props.onChange(_.mapValues(fields, ({value}) => value));

const ComponentForm = ({
                         t,
                         component,
                         mode,
                         disableAnnotations,
                         type,
                         form,
                         dimensions,
                         groups,
                         notDisplayedVal,
                         defaultVal,
                         defaultTimeDimensionStart,
                         defaultTimeDimensionEnd,
                         defaultTimeDimensionLastNObservations,
                         defaultTimeDimensionLastNPeriods,
                         defaultTimeDimensionMode,
                         onChange,
                         onNotDisplayedValChange,
                         onDefaultValChange,
                         onConceptSelect,
                         onConceptSchemeSelect,
                         onCodelistSelect,
                         onNotDisplayedCodesSelectorShow,
                         onDefaultCodesSelectorShow,
                         onDefaultTimeDimensionStartChange,
                         onDefaultTimeDimensionEndChange,
                         onDefaultTimeDimensionLastNObservationsChange,
                         onDefaultTimeDimensionLastNPeriodsChange,
                         onDefaultTimeDimensionModeChange,
                         onHierarchicalCodelistSelect
                       }) =>
  <Fragment>
    <Form>
      {type === DSD_COMPONENT_TYPE_DIMENSION && (
        <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
          <Col span={SPAN_FULL}>
            <Form.Item
              className="form-item-required"
              label={t('data.dsd.dimension.type.label')}
              {...formItemLayout}
            >
              {form.getFieldDecorator('type')(
                <Select
                  showSearch
                  filterOption={(inputValue, {props}) =>
                    props.title.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  disabled={mode === ARTEFACT_FORM_MODE_READ || mode === ARTEFACT_FORM_MODE_EDIT}
                  title={form.getFieldValue('type')}
                >
                  {[
                    DSD_DIMENSION_TYPE_NORMAL,
                    DSD_DIMENSION_TYPE_FREQUENCY,
                    DSD_DIMENSION_TYPE_TIME,
                    DSD_DIMENSION_TYPE_MEASURE
                  ].map(opt =>
                    <Select.Option
                      key={opt}
                      value={opt}
                      title={getSdmxDimensionTypesTranslations(t)[opt]}
                    >
                      {getSdmxDimensionTypesTranslations(t)[opt]}
                    </Select.Option>
                  )}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>
      )}
      <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
        <Col span={SPAN_FULL}>
          <Form.Item
            className="form-item-required"
            label={t('data.dsd.component.id.label')}
            validateStatus={component && isIDValidWithHelp(t, component.id).help ? "warning" : null}
            help={component && isIDValidWithHelp(t, component.id).help}
            {...formItemLayout}
          >
            {form.getFieldDecorator('id', {
              getValueFromEvent: e => e.target.value.toUpperCase()
            })(
              <Input
                disabled={
                  mode === ARTEFACT_FORM_MODE_READ || mode === ARTEFACT_FORM_MODE_EDIT ||
                  type === DSD_COMPONENT_TYPE_MEASURE ||
                  (
                    type === DSD_COMPONENT_TYPE_DIMENSION &&
                    (component.type === DSD_DIMENSION_TYPE_FREQUENCY || component.type === DSD_DIMENSION_TYPE_TIME)
                  )
                }
                title={form.getFieldValue('id')}
                style={{textTransform: "uppercase"}}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
      {(type === DSD_COMPONENT_TYPE_DIMENSION || type === DSD_COMPONENT_TYPE_ATTRIBUTE) && (
        <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
          <Col span={SPAN_FULL}>
            <Form.Item
              label={t('data.dsd.component.order.label')}
              {...formItemLayout}
            >
              {form.getFieldDecorator('order')(
                <InputNumber
                  disabled={mode === ARTEFACT_FORM_MODE_READ}
                  title={form.getFieldValue('order')}
                  min={0}
                  style={{width: "100%"}}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
      )}
      {(type === DSD_COMPONENT_TYPE_MEASURE ||
        type === DSD_COMPONENT_TYPE_DIMENSION || type === DSD_COMPONENT_TYPE_ATTRIBUTE) && (
        <Fragment>
          <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
            <Col span={SPAN_FULL}>
              <Form.Item
                className={"form-item-required"}
                label={t('data.dsd.component.concept.label')}
                {...formItemLayout}
              >
                {form.getFieldDecorator('concept')(
                  <Selector
                    disabled={mode === ARTEFACT_FORM_MODE_READ}
                    title={form.getFieldValue('concept')}
                    onSelect={onConceptSelect}
                    onReset={() => onChange({concept: null})}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
          {(
            type !== DSD_COMPONENT_TYPE_DIMENSION ||
            (component.type === DSD_DIMENSION_TYPE_NORMAL || component.type === DSD_DIMENSION_TYPE_FREQUENCY)
          ) && (
            <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
              <Col span={SPAN_FULL}>
                <Form.Item
                  label={t('data.dsd.component.codelist.label')}
                  {...formItemLayout}
                >
                  {form.getFieldDecorator('representation')(
                    <Selector
                      disabled={mode === ARTEFACT_FORM_MODE_READ}
                      title={form.getFieldValue('representation')}
                      onSelect={onCodelistSelect}
                      onReset={() => onChange({representation: null})}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>
          )}
          {type === DSD_COMPONENT_TYPE_DIMENSION && component.type === DSD_DIMENSION_TYPE_MEASURE && (
            <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
              <Col span={SPAN_FULL}>
                <Form.Item
                  label={t('data.dsd.component.conceptScheme.label')}
                  className={"form-item-required"}
                  {...formItemLayout}
                >
                  {form.getFieldDecorator('representation')(
                    <Selector
                      disabled={mode === ARTEFACT_FORM_MODE_READ}
                      title={form.getFieldValue('representation')}
                      onSelect={onConceptSchemeSelect}
                      onReset={() => onChange({representation: null})}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>
          )}
        </Fragment>
      )}
      {type === DSD_COMPONENT_TYPE_GROUP && (
        <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
          <Col span={SPAN_FULL}>
            <Form.Item
              className="form-item-required"
              label={t('data.dsd.group.dimensions.label')}
              {...formItemLayout}
            >
              {form.getFieldDecorator('groupDimensions')(
                <Select
                  mode="multiple"
                  disabled={mode === ARTEFACT_FORM_MODE_READ}
                  title={form.getFieldValue('groupDimensions')}
                >
                  {(dimensions || []).map(dim =>
                    <Select.Option key={dim.id} value={dim.id}>{dim.id}</Select.Option>
                  )}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>
      )}
      {type === DSD_COMPONENT_TYPE_ATTRIBUTE && (
        <Fragment>
          <Row gutter={GUTTER_MD} style={{marginTop: MARGIN_MD, marginBottom: MARGIN_MD}}>
            <Col span={SPAN_FULL}>
              <Form.Item
                className="form-item-required"
                label={t('data.dsd.attribute.assignmentStatus.label')}
                {...formItemLayout}
              >
                {form.getFieldDecorator('assignmentStatus')(
                  <Select
                    showSearch
                    filterOption={(inputValue, {props}) =>
                      props.title.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    disabled={mode === ARTEFACT_FORM_MODE_READ}
                    title={form.getFieldValue('assignmentStatus')}
                  >
                    {["Conditional", "Mandatory"].map(opt =>
                      <Select.Option key={opt} value={opt} title={opt}>{opt}</Select.Option>
                    )}
                  </Select>
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
            <Col span={SPAN_FULL}>
              <Form.Item
                className="form-item-required"
                label={t('data.dsd.attribute.attachmentLevel.label')}
                {...formItemLayout}
              >
                {form.getFieldDecorator('attachmentLevel')(
                  <Select
                    showSearch
                    filterOption={(inputValue, {props}) =>
                      props.title.toLowerCase().includes(inputValue.toLowerCase())
                    }
                    disabled={mode === ARTEFACT_FORM_MODE_READ}
                    title={form.getFieldValue('attachmentLevel')}
                  >
                    {[
                      DSD_ATTRIBUTE_ATTACHMENT_LEVEL_OBSERVATION,
                      DSD_ATTRIBUTE_ATTACHMENT_LEVEL_DATASET,
                      DSD_ATTRIBUTE_ATTACHMENT_LEVEL_SERIES,
                      DSD_ATTRIBUTE_ATTACHMENT_LEVEL_GROUP
                    ].map(opt =>
                      <Select.Option
                        key={opt}
                        value={opt}
                        title={getSdmxAttributeAttachmentLevelTranslations(t)[opt]}
                      >
                        {getSdmxAttributeAttachmentLevelTranslations(t)[opt]}
                      </Select.Option>
                    )}
                  </Select>
                )}
              </Form.Item>
            </Col>
          </Row>
          {form.getFieldValue("attachmentLevel") === "Series" && (
            <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
              <Col span={SPAN_FULL}>
                <Form.Item
                  className="form-item-required"
                  label={t('data.dsd.attribute.dimensions.label')}
                  {...formItemLayout}
                >
                  {form.getFieldDecorator('attachedDimensions')(
                    <Select
                      mode="multiple"
                      disabled={mode === ARTEFACT_FORM_MODE_READ}
                      title={form.getFieldValue('attachedDimensions')}
                    >
                      {(dimensions || []).map(({id}) =>
                        <Select.Option key={id} value={id}>{id}</Select.Option>
                      )}
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
          )}
          {form.getFieldValue("attachmentLevel") === "Group" && (
            <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
              <Col span={SPAN_FULL}>
                <Form.Item
                  className="form-item-required"
                  label={t('data.dsd.attribute.group.label')}
                  {...formItemLayout}
                >
                  {form.getFieldDecorator('attachmentGroup')(
                    <Select
                      showSearch
                      filterOption={(inputValue, {props}) =>
                        props.title.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      disabled={mode === ARTEFACT_FORM_MODE_READ}
                      title={form.getFieldValue('attachmentGroup')}
                    >
                      {(groups || []).map(({id}) =>
                        <Select.Option key={id} value={id} title={id}>{id}</Select.Option>
                      )}
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
          )}
        </Fragment>
      )}
      {(type === DSD_COMPONENT_TYPE_DIMENSION || type === DSD_COMPONENT_TYPE_ATTRIBUTE) && (
        <Row style={{marginBottom: MARGIN_MD}}>
          <Col span={SPAN_ONE_THIRD}>
            <Form.Item
              label={t('data.dsd.component.notDisplayed.label')}
              labelCol={{span: SPAN_THREE_QUARTERS}}
              wrapperCol={{span: SPAN_ONE_QUARTER}}
            >
              <Checkbox
                disabled={disableAnnotations}
                checked={notDisplayedVal !== null}
                onChange={e => {
                  if (e.target.checked) {
                    onNotDisplayedValChange([]);
                  } else {
                    onNotDisplayedValChange(null);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {component.representation && notDisplayedVal !== null && (
            <Col span={SPAN_TWO_THIRDS}>
              <Form.Item
                label={t('data.dsd.component.notDisplayedItems.label')}
                labelCol={{span: SPAN_ONE_QUARTER}}
                wrapperCol={{span: SPAN_THREE_QUARTERS}}
              >
                <Selector
                  disabled={disableAnnotations}
                  value={notDisplayedVal.length > 0 ? notDisplayedVal.join("+") : null}
                  detailTitle={t('reduxComponents.dsdDetailModal.dsdDetail.componentForm.annotationsCodesSelector.detail.title')}
                  selectTitle={t('reduxComponents.dsdDetailModal.dsdDetail.componentForm.annotationsCodesSelector.select.title')}
                  resetTitle={t('reduxComponents.dsdDetailModal.dsdDetail.componentForm.annotationsCodesSelector.reset.title')}
                  onDetail={() => onNotDisplayedCodesSelectorShow(getArtefactTripletFromUrn(component.representation))}
                  onSelect={() => onNotDisplayedCodesSelectorShow(getArtefactTripletFromUrn(component.representation))}
                  onReset={() => onNotDisplayedValChange([])}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      )}
      {type === DSD_COMPONENT_TYPE_DIMENSION && (
        <Fragment>
          <Row style={{marginBottom: MARGIN_MD}}>
            <Col span={SPAN_ONE_THIRD}>
              <Form.Item
                label={t('data.dsd.dimension.default.label')}
                labelCol={{span: SPAN_THREE_QUARTERS}}
                wrapperCol={{span: SPAN_ONE_QUARTER}}
              >
                <Checkbox
                  disabled={disableAnnotations}
                  checked={defaultVal !== null}
                  onChange={e => {
                    if (e.target.checked) {
                      onDefaultValChange([]);
                    } else {
                      onDefaultValChange(null);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {defaultVal !== null && (
              <Col span={SPAN_TWO_THIRDS}>
                {component.type !== DSD_DIMENSION_TYPE_TIME && component.representation && (
                  <Form.Item
                    label={t('data.dsd.dimension.defaultItems.label')}
                    labelCol={{span: SPAN_ONE_QUARTER}}
                    wrapperCol={{span: SPAN_THREE_QUARTERS}}
                  >
                    <Selector
                      disabled={disableAnnotations}
                      value={defaultVal.length > 0 ? defaultVal.join("+") : null}
                      detailTitle={t('reduxComponents.dsdDetailModal.dsdDetail.componentForm.annotationsCodesSelector.detail.title')}
                      selectTitle={t('reduxComponents.dsdDetailModal.dsdDetail.componentForm.annotationsCodesSelector.select.title')}
                      resetTitle={t('reduxComponents.dsdDetailModal.dsdDetail.componentForm.annotationsCodesSelector.reset.title')}
                      onDetail={() => onDefaultCodesSelectorShow(getArtefactTripletFromUrn(component.representation))}
                      onSelect={() => onDefaultCodesSelectorShow(getArtefactTripletFromUrn(component.representation))}
                      onReset={() => onDefaultValChange([])}
                    />
                  </Form.Item>
                )}
                {component.type === DSD_DIMENSION_TYPE_TIME && (
                  <Fragment>
                    <Row style={{marginBottom: MARGIN_MD}} type="flex" justify="end">
                      <Col>
                        <Form.Item>
                          <Radio.Group
                            disabled={disableAnnotations}
                            value={defaultTimeDimensionMode}
                            onChange={({target}) => {
                              if (
                                defaultTimeDimensionStart ||
                                defaultTimeDimensionEnd ||
                                defaultTimeDimensionLastNObservations ||
                                defaultTimeDimensionLastNPeriods
                              ) {
                                Modal.confirm({
                                  title: t('reduxComponents.dsdDetailModal.dsdDetail.componentForm.timeDimensionModeChangeConfirm.title'),
                                  content: t('reduxComponents.dsdDetailModal.dsdDetail.componentForm.timeDimensionModeChangeConfirm.content'),
                                  onOk() {
                                    onDefaultTimeDimensionModeChange(target.value)
                                  },
                                  cancelText: t('commons.buttons.cancel.title')
                                })
                              } else {
                                onDefaultTimeDimensionModeChange(target.value)
                              }
                            }}
                          >
                            <Radio.Button value={TIME_DIMENSION_DEFAULT_ANNOTATION_MODE_RANGE}>
                              {t('data.dsd.timDimension.mode.values.range')}
                            </Radio.Button>
                            <Radio.Button
                              value={TIME_DIMENSION_DEFAULT_ANNOTATION_MODE_LAST_N_PERIODS}>
                              {t('data.dsd.timDimension.mode.values.lastNPeriods')}
                            </Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                    {defaultTimeDimensionMode === TIME_DIMENSION_DEFAULT_ANNOTATION_MODE_RANGE
                      ? (
                        <Fragment>
                          <Row style={{marginBottom: MARGIN_MD}}>
                            <Col span={SPAN_HALF}>
                              <Form.Item
                                label={t('data.dsd.timDimension.defaultTimeStart.label')}
                                labelCol={{span: SPAN_ONE_QUARTER}}
                                wrapperCol={{span: SPAN_THREE_QUARTERS}}
                              >
                                <LocalizedMonthPicker
                                  format="YYYY-MM"
                                  value={defaultTimeDimensionStart ? moment(defaultTimeDimensionStart) : null}
                                  disabled={disableAnnotations}
                                  onChange={val => onDefaultTimeDimensionStartChange(val ? val.format("YYYY-MM") : null)}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={SPAN_HALF}>
                              <Form.Item
                                label={t('data.dsd.timDimension.defaultTimeEnd.label')}
                                labelCol={{span: SPAN_ONE_QUARTER}}
                                wrapperCol={{span: SPAN_THREE_QUARTERS}}
                              >
                                <LocalizedMonthPicker
                                  format="YYYY-MM"
                                  value={defaultTimeDimensionEnd ? moment(defaultTimeDimensionEnd) : null}
                                  disabled={disableAnnotations}
                                  onChange={val => onDefaultTimeDimensionEndChange(val ? val.format("YYYY-MM") : null)}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row style={{marginBottom: MARGIN_MD}}>
                            <Col span={SPAN_FULL}>
                              <Form.Item
                                label={t('data.dsd.timDimension.defaultLastNObservations.label')}
                                labelCol={{span: SPAN_THREE_QUARTERS}}
                                wrapperCol={{span: SPAN_ONE_QUARTER}}
                              >
                                <InputNumber
                                  style={{width: "100%"}}
                                  min={0}
                                  precision={0}
                                  value={defaultTimeDimensionLastNObservations}
                                  disabled={disableAnnotations}
                                  onChange={val => onDefaultTimeDimensionLastNObservationsChange(val || null)}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Fragment>
                      )
                      : (
                        <Row style={{marginBottom: MARGIN_MD}}>
                          <Col span={SPAN_FULL}>
                            <Form.Item
                              label={t('data.dsd.timDimension.defaultLastNPeriods.label')}
                              labelCol={{span: SPAN_THREE_QUARTERS}}
                              wrapperCol={{span: SPAN_ONE_QUARTER}}
                            >
                              <InputNumber
                                style={{width: "100%"}}
                                min={0}
                                precision={0}
                                value={defaultTimeDimensionLastNPeriods}
                                disabled={disableAnnotations}
                                onChange={val => onDefaultTimeDimensionLastNPeriodsChange(val || null)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                  </Fragment>
                )}
              </Col>
            )}
          </Row>
        </Fragment>
      )}
      {(type === DSD_COMPONENT_TYPE_DIMENSION || type === DSD_COMPONENT_TYPE_ATTRIBUTE ) && component.representation && (
        <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
          <Col span={form.getFieldValue('hclTriplet') ? 14 : SPAN_FULL}>
            <Form.Item
              label={t('data.dsd.component.hcl.label')}
              {...formItemLayout}
            >
              {form.getFieldDecorator('hclTriplet')(
                <Selector                  
                  title={form.getFieldValue('hclTriplet')}
                  onSelect={onHierarchicalCodelistSelect}
                  onReset={() => onChange({hclTriplet: null, hierId: null})}
                />
              )}
            </Form.Item>
          </Col>
          { form.getFieldValue('hclTriplet') && (
            <Col span={10}>
              <Form.Item
                label={t("data.dsd.component.hierId.label")}
                {...formItemLayout}
              > {form.getFieldDecorator('hierId')(
                <Input
                  disabled={!form.getFieldValue('hclTriplet')} 
                  title={form.getFieldValue('hierId')}
                  style={{textTransform: "uppercase"}}                 
                />
              )}
              </Form.Item>
            </Col>
          )}
        </Row>
      )}
    </Form>
    <AnnotationsForm
      annotations={component ? component.annotations : null}
      onChange={onChange}
      disabled={disableAnnotations}
    />
  </Fragment>;

export default compose(
  translate(),
  Form.create({mapPropsToFields, onFieldsChange})
)(ComponentForm);
